import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Package, MapPin, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OrderTimeline } from "@/components/OrderTimeline";
import { formatINR, waLink } from "@/config";

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/${orderId.trim().toUpperCase()}`);
      setOrder(data);
    } catch {
      toast.error("Order not found. Please check your Order ID.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-widest text-[#C2410C] font-semibold">Order Status</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F130B] mt-1">Track Your Order</h1>
        <p className="text-sm text-[#6B5B52] mt-2">Enter your Order ID to see the latest status.</p>
      </div>

      <form onSubmit={track} className="flex gap-2 max-w-md mx-auto">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="e.g. SDF260909ABCDE" data-testid="track-order-input" className="w-full rounded-full border border-[#E7E0D6] bg-white pl-9 pr-4 py-3 text-sm outline-none focus:border-[#C2410C]" />
        </div>
        <button disabled={loading} data-testid="track-order-btn" className="rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold px-6 disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Track"}
        </button>
      </form>

      {order && (
        <div className="mt-10 rounded-2xl bg-white border border-[#E7E0D6] p-6" data-testid="track-result">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
            <div>
              <p className="font-semibold text-[#1F130B]">{order.order_id}</p>
              <p className="text-xs text-[#6B5B52]">Placed on {new Date(order.created_at).toLocaleDateString("en-IN")}</p>
            </div>
            <span className="rounded-full bg-[#FEF3C7] text-[#9A3412] font-semibold px-4 py-1.5 text-sm capitalize">{order.status}</span>
          </div>
          <OrderTimeline status={order.status} />
          <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl bg-[#FAF6F0] p-4">
              <p className="flex items-center gap-2 font-semibold text-[#1F130B]"><Package size={15} className="text-[#C2410C]" /> Items</p>
              {order.items.map((it, i) => (
                <p key={i} className="text-[#6B5B52] mt-1">{it.name} ({it.weight_label}) ×{it.qty}</p>
              ))}
              <p className="font-bold text-[#1F130B] mt-2">Total: {formatINR(order.total)}</p>
            </div>
            <div className="rounded-xl bg-[#FAF6F0] p-4">
              <p className="flex items-center gap-2 font-semibold text-[#1F130B]"><MapPin size={15} className="text-[#C2410C]" /> Delivery</p>
              <p className="text-[#6B5B52] mt-1">{order.customer.city}, {order.customer.state} - {order.customer.pincode}</p>
              <p className="text-[#6B5B52] mt-1">Est: {order.est_delivery}</p>
            </div>
          </div>
          <a href={waLink(`Hi! I want an update on my order ${order.order_id}.`)} target="_blank" rel="noreferrer" className="inline-flex mt-5 items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-6 py-2.5 text-sm">
            <MessageCircle size={16} /> Ask for update on WhatsApp
          </a>
        </div>
      )}

      <p className="text-center text-sm text-[#6B5B52] mt-8">Can't find your Order ID? <Link to="/contact" className="text-[#C2410C] font-semibold">Contact us</Link></p>
    </div>
  );
}
